/**
 * Shared helpers for Model Lab 3D visualization.
 *
 * Shared by the inline Model Builder preview and the dedicated full-screen
 * 3D viewer.
 */

import type { Layer3DSpec } from './NeuralNetwork3D';
import type { ModelInternals, ModelTree } from '../../types';

export const DL_ALGOS = new Set([
  'lstm', 'bilstm', 'gru', 'xlstm', 'transformer', 'temporal_fusion_transformer',
  'informer', 'patch_tst', 'cnn_1d', 'tcn', 'conv_lstm', 'wavenet', 'nbeats',
  'autoencoder', 'vae', 'attention_network', 'mlp',
]);

export const TREE_ALGOS = new Set([
  'random_forest', 'gradient_boosting', 'extra_trees', 'adaboost',
  'decision_tree', 'xgboost', 'lightgbm',
]);

export const humanize = (s: string): string =>
  s
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

/** Map sklearn/casual algorithm strings to canonical snake_case keys. */
export const normalizeAlgorithm = (raw: string): string => {
  const s = (raw || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!s) return '';
  if (s.includes('bilstm')) return 'bilstm';
  if (s.includes('lstm')) return s.includes('conv') ? 'conv_lstm' : 'lstm';
  if (s.includes('gru')) return 'gru';
  if (s.includes('xlstm')) return 'xlstm';
  if (s.includes('transformer')) return 'transformer';
  if (s.includes('tft') || s.includes('temporalfusion')) return 'temporal_fusion_transformer';
  if (s.includes('informer')) return 'informer';
  if (s.includes('patchtst')) return 'patch_tst';
  if (s.includes('tcn')) return 'tcn';
  if (s.includes('wavenet')) return 'wavenet';
  if (s.includes('nbeats')) return 'nbeats';
  if (s.includes('autoencoder') || s === 'ae') return 'autoencoder';
  if (s === 'vae' || s.includes('variationalautoencoder')) return 'vae';
  if (s.includes('attention')) return 'attention_network';
  if (s.includes('cnn') || s.includes('conv1d') || s.includes('conv1') || s.includes('convnet')) return 'cnn_1d';
  if (s === 'mlp' || s.includes('multilayerperceptron') || s.includes('feedforward') || s.includes('dense')) return 'mlp';
  if (s.includes('randomforest') || s === 'rf' || s === 'rfclassifier' || s === 'rfregressor') return 'random_forest';
  if (s.includes('extratrees') || s === 'et') return 'extra_trees';
  if (s.includes('gradientboost') || s === 'gbm' || s === 'gbr' || s === 'gbc') return 'gradient_boosting';
  if (s.includes('adaboost')) return 'adaboost';
  if (s.includes('xgb') || s.includes('xgboost')) return 'xgboost';
  if (s.includes('lightgbm') || s === 'lgbm' || s === 'lgb') return 'lightgbm';
  if (s.includes('decisiontree') || s === 'dt') return 'decision_tree';
  return s;
};

export type LayerSpec = {
  kind: string;
  label: string;
  units: number;
  color: string;
  detail?: string;
};

export const deriveDLLayers = (
  algo: string,
  hp: Record<string, any>,
  inputSize: number,
  outputSize: number | undefined,
  windowSize: number | undefined,
): LayerSpec[] => {
  const inp = inputSize || Number(hp.input_size) || 0;
  const out = outputSize || Number(hp.output_size) || 1;
  const layers: LayerSpec[] = [];
  const push = (l: LayerSpec) => layers.push(l);

  push({
    kind: 'input',
    label: windowSize ? `Input (seq ${windowSize} × ${inp})` : `Input (${inp})`,
    units: inp || 8,
    color: '#1976d2',
  });

  const hs = Number(hp.hidden_size) || 64;
  const nl = Number(hp.num_layers) || 2;
  const d = Number(hp.d_model) || Number(hp.hidden_size) || 64;
  const nhead = Number(hp.nhead) || Number(hp.num_heads) || 4;
  const dff = Number(hp.dim_feedforward) || Number(hp.hidden_size) * 4 || 256;
  const dropout = Number(hp.dropout) || 0;
  const filters = Number(hp.filters) || Number(hp.num_channels) || 64;
  const kernel = Number(hp.kernel_size) || 3;

  const addDropout = () => {
    if (dropout > 0) {
      push({ kind: 'dropout', label: `Dropout (${dropout})`, units: 0, color: '#9e9e9e' });
    }
  };

  if (algo === 'lstm' || algo === 'bilstm' || algo === 'gru') {
    const bi = algo === 'bilstm' || hp.bidirectional === 'true' || hp.bidirectional === true;
    for (let i = 0; i < nl; i++) {
      push({
        kind: 'recurrent',
        label: `${bi ? 'Bi-' : ''}${algo.toUpperCase()} layer ${i + 1}`,
        units: bi ? hs * 2 : hs,
        color: '#2e7d32',
        detail: `hidden ${hs}${bi ? ' ×2' : ''}`,
      });
      addDropout();
    }
  } else if (algo === 'transformer' || algo === 'attention_network') {
    for (let i = 0; i < nl; i++) {
      push({
        kind: 'attention',
        label: `Multi-Head Attention ${i + 1}`,
        units: d,
        color: '#7b1fa2',
        detail: `d_model ${d} · heads ${nhead}`,
      });
      push({
        kind: 'ff',
        label: `FeedForward ${i + 1}`,
        units: dff,
        color: '#ab47bc',
        detail: `dim ${dff}`,
      });
      addDropout();
    }
  } else if (algo === 'cnn_1d' || algo === 'tcn') {
    const ncl = Number(hp.num_conv_layers) || Number(hp.num_layers) || 3;
    for (let i = 0; i < ncl; i++) {
      push({
        kind: 'conv',
        label: `Conv1D ${i + 1}`,
        units: filters,
        color: '#ef6c00',
        detail: `filters ${filters} · k=${kernel}`,
      });
      addDropout();
    }
  } else if (algo === 'mlp') {
    const sizes = String(hp.hidden_layer_sizes || '100,50')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => n > 0);
    sizes.forEach((s, i) => {
      push({
        kind: 'dense',
        label: `Dense ${i + 1}`,
        units: s,
        color: '#2e7d32',
        detail: `${s} neurons · ReLU`,
      });
      addDropout();
    });
  } else {
    push({ kind: 'hidden', label: 'Hidden layer 1', units: hs, color: '#2e7d32' });
    addDropout();
    push({ kind: 'hidden', label: 'Hidden layer 2', units: hs, color: '#2e7d32' });
  }

  push({ kind: 'output', label: `Output (${out})`, units: out, color: '#0288d1' });
  return layers;
};

export const buildTreeEnsembleLayers = (
  algorithm: string,
  hyper: Record<string, any>,
  nFeatures: number,
): Layer3DSpec[] => {
  const nTrees = Number(hyper.n_estimators) || 100;
  const maxDepth = Math.max(2, Number(hyper.max_depth) || 6);
  const showTrees = Math.min(nTrees, 48);

  const layers: Layer3DSpec[] = [];
  layers.push({
    label: 'Input features',
    units: Math.max(nFeatures, 4),
    color: '#1976d2',
  });
  for (let d = 0; d < maxDepth; d++) {
    const nodesPerTree = Math.pow(2, d);
    const units = Math.min(showTrees * nodesPerTree, 64);
    layers.push({
      label: d === maxDepth - 1 ? 'Leaf nodes' : `Depth ${d + 1}`,
      units,
      color: d === maxDepth - 1 ? '#ff9800' : '#2e7d32',
      detail: `${showTrees} trees × ${nodesPerTree} node${nodesPerTree === 1 ? '' : 's'}`,
    });
  }
  layers.push({
    label: `${humanize(algorithm)} vote`,
    units: 1,
    color: '#0288d1',
    detail: 'aggregated prediction',
  });
  return layers;
};

/** Build tree layers from real internals: one layer per tree, each node shows split info + impurity. */
const buildTreeLayersFromInternals = (
  trees: ModelTree[],
  algorithm: string,
  maxTrees: number = 6,
): Layer3DSpec[] => {
  const layers: Layer3DSpec[] = [];
  trees.slice(0, maxTrees).forEach((tree, tIdx) => {
    const nodes = tree.nodes || [];
    const labels: string[] = [];
    const values: number[] = [];
    nodes.forEach((n) => {
      if (n.is_leaf) {
        const valStr = n.value && n.value.length
          ? `leaf [${n.value.slice(0, 3).map((v) => v.toFixed(2)).join(', ')}${n.value.length > 3 ? '…' : ''}]`
          : 'leaf';
        labels.push(`Node ${n.id} · ${valStr}`);
        values.push(n.impurity ?? 0);
      } else {
        const thr = typeof n.threshold === 'number' ? n.threshold.toFixed(4) : '?';
        labels.push(`Node ${n.id} · ${n.feature || `f${n.feature_idx}`} ≤ ${thr}`);
        values.push(n.impurity ?? 0);
      }
    });
    layers.push({
      label: `Tree ${tIdx + 1}${tree.max_depth ? ` (depth ${tree.max_depth})` : ''}`,
      units: Math.max(nodes.length, 1),
      color: tIdx % 2 === 0 ? '#2e7d32' : '#558b2f',
      detail: `${tree.node_count ?? nodes.length} nodes`,
      neuronLabels: labels,
      neuronValues: values,
      valueFormat: 'number',
      valueUnit: 'impurity',
    });
  });
  return layers;
};

/** Build DL layers from real internals (sklearn MLP): each neuron shows mean|weight| and bias. */
const buildDLLayersFromInternals = (internals: ModelInternals): Layer3DSpec[] => {
  const layers: Layer3DSpec[] = [];
  internals.dl_layers.forEach((layer, i) => {
    const neurons = layer.neurons || [];
    const labels = neurons.map(
      (n, j) => `Neuron ${j} · bias ${n.bias.toFixed(3)}`,
    );
    const values = neurons.map((n) => n.mean_abs_weight);
    layers.push({
      label: i === internals.dl_layers.length - 1 ? 'Output' : `Dense ${i + 1}`,
      units: layer.neuron_count,
      color: i === internals.dl_layers.length - 1 ? '#0288d1' : '#2e7d32',
      detail: `shape ${layer.shape.join('×')} · w∈[${layer.weight_min.toFixed(2)}, ${layer.weight_max.toFixed(2)}]`,
      neuronLabels: labels,
      neuronValues: values,
      valueFormat: 'number',
      valueUnit: 'mean|w|',
    });
  });
  return layers;
};

/** Compute the 3D layers from asset+versions+jobs, trying multiple sources. */
export const buildModel3DLayers = (
  asset: any,
  versions: any[],
  jobs: any[],
  internals?: ModelInternals | null,
): { layers: Layer3DSpec[]; algorithm: string; rawAlgorithm: string } => {
  const sorted = [...(versions || [])].sort(
    (a, b) => (b.version_number || 0) - (a.version_number || 0),
  );
  const version =
    sorted.find((v) => v.metrics && Object.keys(v.metrics).length > 0) || sorted[0];
  const sourceJob = version
    ? (jobs || []).find((j) => j.generated_model_version_id === version.id)
    : null;

  const summary = version?.training_summary || {};
  const metrics = version?.metrics || {};
  const rawAlgorithm =
    summary.algorithm ||
    sourceJob?.config?.algorithm ||
    asset?.runtime_spec?.algorithm ||
    '';
  const algorithm = normalizeAlgorithm(String(rawAlgorithm));
  const hyper = sourceJob?.config?.hyperparameters || summary.hyperparameters || {};
  const featureColumns: string[] =
    summary.feature_columns || sourceJob?.config?.feature_columns || [];
  const targetColumn: string =
    summary.target_column || sourceJob?.config?.target_column || 'target';
  const classes: string[] | undefined =
    summary.classes || summary.class_labels || metrics.class_labels;
  const inputSize = summary.input_size || featureColumns.length;
  const outputSize = summary.output_size;
  const windowSize = summary.window_size;

  // Feature importance map (name or index -> importance value)
  const featureImportanceRaw = metrics.feature_importance;
  const featureImportance: Record<string, number> = {};
  if (featureImportanceRaw) {
    if (Array.isArray(featureImportanceRaw)) {
      featureImportanceRaw.forEach((v: any, i: number) => {
        const name = featureColumns[i] || `feature_${i}`;
        featureImportance[name] = Number(v) || 0;
      });
    } else if (typeof featureImportanceRaw === 'object') {
      Object.entries(featureImportanceRaw).forEach(([k, v]) => {
        featureImportance[k] = Number(v) || 0;
      });
    }
  }
  // Merge in internals feature_importance if present (authoritative source from actual model)
  if (internals?.feature_importance) {
    internals.feature_importance.forEach((fi) => {
      featureImportance[fi.feature] = fi.importance;
    });
  }

  // Prefer encoded_columns from internals if available (these match what the model actually sees)
  const effectiveFeatureColumns =
    internals?.encoded_columns && internals.encoded_columns.length
      ? internals.encoded_columns
      : featureColumns;
  const effectiveClasses = internals?.classes || classes;

  const inputNeuronLabels = effectiveFeatureColumns.slice(0, 64);
  const inputNeuronValues = inputNeuronLabels.map(
    (name) => featureImportance[name] ?? 0,
  );
  const hasImportance = inputNeuronValues.some((v) => v > 0);

  const outputNeuronLabels = (() => {
    if (effectiveClasses && effectiveClasses.length) return effectiveClasses.map((c) => String(c));
    if (asset?.problem_type === 'CLASSIFICATION' && outputSize && outputSize > 1) {
      return Array.from({ length: outputSize }, (_, i) => `class_${i}`);
    }
    return [targetColumn];
  })();

  let layers: Layer3DSpec[];

  if (DL_ALGOS.has(algorithm)) {
    // If we have real per-layer weight stats from the artifact, build from those.
    if (internals && internals.dl_layers && internals.dl_layers.length) {
      const inputLayer: Layer3DSpec = {
        label: windowSize ? `Input (seq ${windowSize} × ${inputSize || inputNeuronLabels.length})` : `Input (${inputSize || inputNeuronLabels.length})`,
        units: Math.max(inputSize || inputNeuronLabels.length, 4),
        color: '#1976d2',
        neuronLabels: inputNeuronLabels,
        ...(hasImportance
          ? {
              neuronValues: inputNeuronValues,
              valueFormat: 'percent' as const,
              valueUnit: 'importance',
            }
          : {}),
      };
      const dlLayers = buildDLLayersFromInternals(internals);
      // Tag last DL layer with class labels
      if (dlLayers.length && outputNeuronLabels.length) {
        const last = dlLayers[dlLayers.length - 1];
        last.neuronLabels = outputNeuronLabels.slice(0, last.units).concat(
          // keep original neuron-info if more neurons than labels
          (last.neuronLabels || []).slice(outputNeuronLabels.length),
        );
      }
      layers = [inputLayer, ...dlLayers];
    } else {
      const dl = deriveDLLayers(algorithm, hyper, inputSize, outputSize, windowSize);
      layers = dl.map((l, idx): Layer3DSpec => {
        const base: Layer3DSpec = {
          label: l.label,
          units: l.units || 1,
          color: l.color,
          kind: l.kind,
          detail: l.detail,
        };
        if (idx === 0 && inputNeuronLabels.length) {
          base.neuronLabels = inputNeuronLabels;
          if (hasImportance) {
            base.neuronValues = inputNeuronValues;
            base.valueFormat = 'percent';
            base.valueUnit = 'importance';
          }
        }
        if (idx === dl.length - 1) {
          base.neuronLabels = outputNeuronLabels.slice(0, l.units);
        }
        return base;
      });
    }
  } else if (TREE_ALGOS.has(algorithm)) {
    // If we have real tree internals, build one layer per tree showing real nodes.
    if (internals && internals.trees && internals.trees.length) {
      const inputLayer: Layer3DSpec = {
        label: 'Input features',
        units: Math.max(inputNeuronLabels.length, 4),
        color: '#1976d2',
        neuronLabels: inputNeuronLabels,
        ...(hasImportance
          ? {
              neuronValues: inputNeuronValues,
              valueFormat: 'percent' as const,
              valueUnit: 'importance',
            }
          : {}),
      };
      const treeLayers = buildTreeLayersFromInternals(internals.trees, algorithm);
      const outputLayer: Layer3DSpec = {
        label: `${humanize(algorithm)} vote`,
        units: Math.max(outputNeuronLabels.length, 1),
        color: '#0288d1',
        detail: 'aggregated prediction',
        neuronLabels: outputNeuronLabels,
      };
      layers = [inputLayer, ...treeLayers, outputLayer];
    } else {
      layers = buildTreeEnsembleLayers(algorithm, hyper, featureColumns.length);
      // Attach feature names + importance to input layer
      if (layers[0] && inputNeuronLabels.length) {
        layers[0].neuronLabels = inputNeuronLabels;
        if (hasImportance) {
          layers[0].neuronValues = inputNeuronValues;
          layers[0].valueFormat = 'percent';
          layers[0].valueUnit = 'importance';
        }
      }
      // Attach class labels to final vote layer
      const last = layers[layers.length - 1];
      if (last && outputNeuronLabels.length) {
        last.neuronLabels = outputNeuronLabels;
      }
      // Tag intermediate depth layers with tree index info
      const showTrees = Math.min(Number(hyper.n_estimators) || 100, 48);
      for (let i = 1; i < layers.length - 1; i++) {
        const l = layers[i];
        const labels: string[] = [];
        for (let n = 0; n < Math.min(l.units, 64); n++) {
          labels.push(`Tree ${(n % showTrees) + 1} · node ${Math.floor(n / showTrees) + 1}`);
        }
        l.neuronLabels = labels;
      }
    }
  } else {
    const isClassification = asset?.problem_type === 'CLASSIFICATION';
    layers = [
      {
        label: 'Input features',
        units: Math.max(featureColumns.length, 4),
        color: '#1976d2',
        neuronLabels: inputNeuronLabels,
        ...(hasImportance
          ? {
              neuronValues: inputNeuronValues,
              valueFormat: 'percent' as const,
              valueUnit: 'importance',
            }
          : {}),
      },
      {
        label: 'Model',
        units: 16,
        color: '#9c27b0',
        detail: String(rawAlgorithm) || 'unknown',
      },
      {
        label: isClassification ? 'Class scores' : 'Prediction',
        units: isClassification ? Math.max(outputNeuronLabels.length, 1) : 1,
        color: '#2e7d32',
        neuronLabels: outputNeuronLabels,
      },
    ];
  }

  return { layers, algorithm, rawAlgorithm: String(rawAlgorithm) };
};
